/**
 * VRG-723 — the geometry and house rules of the OUTREACH CERTIFICATE PNG, as pure data, so the
 * rules are guarded (test/outreach-certificate.guard.spec.ts) rather than remembered.
 *
 * The house style (Spencer, 2026-10-07; saved feedback "Outreach certificates = light mode"):
 *   - ALWAYS LIGHT MODE. The image goes into an email and the page is opened from one; both read
 *     as light. Nothing here reads the viewer's colour scheme: the palette below is fixed.
 *   - The product photo is PADDED, never zoomed or cropped: it is fitted whole ("contain") inside
 *     the tile less a margin of ~10% left and right, 12% top and 20% bottom.
 *   - A visible "Demo certificate" label ON the pixels: the image shows a real brand's product with
 *     a mock identifier, so a screenshot of it must never be mistakable for a live record.
 *     (VRG-726, Spencer 2026-10-20: was "Concept mockup". Every outreach certificate is part of a
 *     demo now, and the email links to that demo's certificate, so the label names what it is.)
 *   - The brand's own logo in the header when it has one, else its name.
 *
 * No React, no DOM, no path aliases: the guard imports this file directly under node's type
 * stripping.
 */

export const OUTREACH_PNG = {
  width: 1080,
  height: 1350,
  /** The fixed LIGHT palette. Deliberately not theme tokens: a dark-mode viewer must get light. */
  colors: {
    page: '#F4F4F5',
    card: '#FFFFFF',
    border: '#E4E4E7',
    tile: '#F4F4F5',
    ink: '#111111',
    muted: '#6B7280',
    faint: '#9CA3AF',
    label: 'rgba(17, 17, 17, 0.72)',
    labelInk: '#FFFFFF',
  },
  card: { x: 70, y: 60, w: 940, h: 1230, radius: 44 },
  header: { h: 128 },
  tile: { x: 120, y: 236, w: 840, h: 640, radius: 28 },
  /** Top of the facts row (labels), measured from the card's bottom edge. */
  factsFromBottom: 96,
  /** Fractions of the tile left empty around the photo (house rule: padded, not zoomed). */
  photoPadding: { left: 0.1, right: 0.1, top: 0.12, bottom: 0.2 },
  label: 'Demo certificate',
} as const

export interface Box {
  x: number
  y: number
  w: number
  h: number
}

/** The area the photo may occupy: the tile less its padding. */
export const photoArea = (): Box => {
  const { tile, photoPadding: p } = OUTREACH_PNG
  return {
    x: tile.x + tile.w * p.left,
    y: tile.y + tile.h * p.top,
    w: tile.w * (1 - p.left - p.right),
    h: tile.h * (1 - p.top - p.bottom),
  }
}

/**
 * Fit an image of `iw × ih` WHOLE inside `box`, centred, aspect kept: "contain", never "cover".
 * Returns the drawn rectangle. A zero-sized image yields the box itself (nothing to draw).
 */
export const fitContain = (iw: number, ih: number, box: Box): Box => {
  if (!(iw > 0) || !(ih > 0)) return box
  const scale = Math.min(box.w / iw, box.h / ih)
  const w = iw * scale
  const h = ih * scale
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h }
}

/** The certificate's short mock identifier: `VERA-` and the first 8 hex of its id, upper-cased. */
export const mockCertificateNumber = (certificateId: string): string =>
  `VERA-${certificateId.replace(/[^0-9a-f]/gi, '').slice(0, 8).toUpperCase()}`

/** A tenant accent too pale to read on white falls back to ink. Relative luminance, sRGB. */
export const readableAccent = (hex: string | null | undefined): string => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex ?? '').trim())
  if (!m) return OUTREACH_PNG.colors.ink
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255)
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
  return luminance > 0.6 ? OUTREACH_PNG.colors.ink : `#${m[1]}`
}

/** `<subdomain>-outreach-certificate.png`, safe for a download attribute. */
export const outreachPngFilename = (subdomain: string): string =>
  `${(subdomain || 'demo').replace(/[^a-z0-9-]+/gi, '-').toLowerCase()}-outreach-certificate.png`
